import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, CheckCircle2, Copy, Loader2, Upload, X, Sparkles, Phone, Map, Search, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { api, fileUrl, publicUploadImage } from '../lib/api';
import { PageHeader, ISSUE_CATEGORIES, categoryLabel, useIssueCategories } from '../components/Shared';
import { HubDisclaimer } from './ActionHub';
import { PinPlacer } from '../components/PinPlacer';
import { toast } from 'sonner';

const STEPS = ['Your Information', 'Issue Details', 'Location', 'CKL Follow-up', 'Public Map Options', 'Review & Submit'];

const EMPTY_FORM = {
  name: '', email: '', phone: '',
  category: '', description: '',
  address: '', location: '', community: '',
  pin: null, pin_exact_public: false,
  contacted_ckl: false, ckl_case_number: '', preferred_followup: 'email',
  consent_public: true, public_title: '', public_description: '', photo_public: false, photos: [],
  cleaned_summary: '', polished_request: '', public_map_summary: '',
  consent_confirmed: false,
};

export default function ReportIssue() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const liveCategories = useIssueCategories();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [result, setResult] = useState(null);
  const [thanksAi, setThanksAi] = useState(null);
  const [thanksAiBusy, setThanksAiBusy] = useState(false);
  const [thanksCfg, setThanksCfg] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM, category: searchParams.get('category') || '' });
  // Phase 97: ward detection preview shown BEFORE final submission
  const [wardCheck, setWardCheck] = useState(null);
  const [wardChecking, setWardChecking] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/communities').then((r) => setCommunities(r.data?.communities || [])).catch(() => {});
  }, []);

  // When the resident reaches Review & Submit, geocode the submitted
  // address/intersection and tell them which ward the location is in.
  useEffect(() => {
    if (step !== 6) return;
    setWardChecking(true);
    setWardCheck(null);
    api.post('/geocode/preview', form.pin
      ? { address: form.address, location: form.location, community: form.community, lat: form.pin.lat, lng: form.pin.lng }
      : { address: form.address, location: form.location, community: form.community })
      .then((r) => setWardCheck(r.data))
      .catch(() => setWardCheck(null))
      .finally(() => setWardChecking(false));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailOk = /.+@.+\..+/.test(form.email);
  const canNext =
    step === 1 ? form.name.trim() && emailOk :
    step === 2 ? form.category && form.description.trim() :
    step === 3 ? form.address.trim() && form.location.trim() :
    true;

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.photos.length >= 3) { toast.error('Maximum 3 photos'); return; }
    setUploading(true);
    try {
      const res = await publicUploadImage(file);
      set('photos', [...form.photos, res.url]);
      toast.success('File added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async () => {
    if (!form.consent_confirmed) { toast.error('Please confirm the consent checkbox'); return; }
    setBusy(true);
    try {
      const title = form.public_title.trim() || (form.cleaned_summary || form.description).slice(0, 90);
      const trackerSlug = (searchParams.get('tracker') || 'ward-8').trim().toLowerCase();
      const { pin, ...rest } = form;
      const res = await api.post('/issues', { ...rest, title, tracker_slug: trackerSlug, pin_lat: pin?.lat ?? null, pin_lng: pin?.lng ?? null });
      setResult(res.data);
      window.scrollTo(0, 0);
      // Next-steps summary (privacy-safe; nothing stored)
      setThanksAiBusy(true);
      api.post('/issues/thank-you-summary', {
        tracking_number: res.data.tracking_number,
        category: form.category,
        description: form.description,
        location: form.location,
        community: form.community,
        contacted_ckl: form.contacted_ckl,
        polished_request: form.polished_request,
      }).then((r) => setThanksAi(r.data)).catch(() => setThanksAi(null)).finally(() => setThanksAiBusy(false));
      // thank-you copy (admin-editable)
      api.get('/campaign/content').then((r) => setThanksCfg(r.data?.thankyou || null)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const copyRequest = () => {
    const text = thanksAi?.polished_request || form.polished_request || form.cleaned_summary || form.description;
    navigator.clipboard?.writeText(text);
    toast.success('Copied. Paste it into an official CKL request');
  };

  if (result) {
    const urgencyCls = thanksAi?.suggested_urgency === 'high' || thanksAi?.suggested_urgency === 'urgent'
      ? 'bg-red-50 text-red-800 border-red-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200';
    return (
      <div>
      <PageHeader kicker="THANK YOU" title={thanksCfg?.title || 'Thank you. Your Ward 8 issue has been received.'} subtitle="Save your tracking number. Here's what happens next." />
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-5">
          {/* Confirmation card */}
          <Card data-testid="thankyou-card">
            <CardContent className="p-6 sm:p-8">
              <div className="text-center">
                <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
                <p className="text-slate-600">Your tracking number is</p>
                <p className="font-mono text-2xl sm:text-3xl font-black text-[#1a2744] mt-2 tracking-wide" data-testid="issue-report-tracking-number">{result.tracking_number}</p>
                {result.ward && (
                  <p className="text-sm font-bold text-[#24406f] mt-2" data-testid="thankyou-ward">Recorded under Ward {result.ward}</p>
                )}
                {result.needs_location_review && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2 inline-block" data-testid="thankyou-location-review">Your location will be placed on the map manually after review. You can place it yourself right now with the pin link below.</p>
                )}
                <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left text-sm" data-testid="thankyou-email-status">
                  {result.receipt_state === 'sent' && <p className="text-emerald-800 font-semibold">A confirmation email with your tracking number and a copy of your report has been handed to the mail server. Check your inbox and spam folder.</p>}
                  {result.receipt_state === 'queued' && <p className="text-slate-700"><strong>Your report is saved.</strong> The confirmation email is queued and will be sent when email sending is switched on. Keep your tracking number above; it works right now.</p>}
                  {result.receipt_state === 'simulated' && <p className="text-slate-700"><strong>Your report is saved.</strong> Email is in test mode on this site, so no confirmation email was sent. Keep your tracking number above; it works right now.</p>}
                  {result.receipt_state === 'failed' && <p className="text-amber-900"><strong>Your report is saved</strong>, but the confirmation email could not be sent right now. Steve's team can resend it. Keep your tracking number above.</p>}
                  {result.receipt_state === 'no_email' && <p className="text-slate-700">No email address was given, so no confirmation email will be sent. Keep your tracking number above.</p>}
                  {(!result.receipt_state || result.receipt_state === 'none') && <p className="text-slate-700">Keep your tracking number above; it works right now.</p>}
                </div>
                {result.public_url && (
                  <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-left" data-testid="thankyou-public-link-box">
                    <p className="text-sm font-bold text-emerald-900">Your report is public.</p>
                    <p className="text-xs text-emerald-900/80 mt-0.5">Anyone with this link can see the public title, status, approved photos and a privacy-safe map pin.</p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                      <Button asChild variant="outline" className="h-10 font-bold btn-press border-emerald-700 text-emerald-900" data-testid="thankyou-public-link"><Link to={result.public_url}>View public report</Link></Button>
                      <Button variant="outline" className="h-10 font-bold btn-press" data-testid="thankyou-copy-public-link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${result.share_url || result.public_url}`); toast.success('Public link copied'); }}><Copy className="h-4 w-4 mr-1.5" /> Copy public link</Button>
                    </div>
                  </div>
                )}
                {result.pin_edit_url && (
                  <div className="mt-3 rounded-xl border border-[#c9d8f0] bg-[#f3f7ff] p-3 text-left" data-testid="thankyou-pin-box">
                    <p className="text-sm font-bold text-[#1a2744]">{result.resident_located ? 'Your pin is saved where you placed it.' : 'Is the map pin in the right spot?'}</p>
                    <p className="text-xs text-slate-600 mt-0.5">You can move your pin any time with this private link (also included in your receipt email). Save it; the link is your key.</p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                      <Button asChild variant="outline" className="h-10 font-bold btn-press border-[#1a2744] text-[#1a2744]" data-testid="thankyou-adjust-pin-link"><Link to={result.pin_edit_url}>{result.resident_located ? 'Review or move my pin' : 'Place or move my pin'}</Link></Button>
                      <Button variant="outline" className="h-10 font-bold btn-press" data-testid="thankyou-copy-pin-link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${result.pin_edit_url}`); toast.success('Pin link copied'); }}><Copy className="h-4 w-4 mr-1.5" /> Copy pin link</Button>
                    </div>
                  </div>
                )}
                <Button
                  variant="outline"
                  className="mt-3 h-10 btn-press font-bold"
                  data-testid="copy-tracking-button"
                  onClick={() => { navigator.clipboard?.writeText(result.tracking_number); toast.success('Tracking number copied'); }}
                >
                  <Copy className="h-4 w-4 mr-1.5" /> Copy number
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 text-center">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Category</p>
                  <p className="text-sm font-bold text-[#1a2744] mt-1" data-testid="thankyou-category">{categoryLabel(form.category)}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Submitted</p>
                  <p className="text-sm font-bold text-[#1a2744] mt-1" data-testid="thankyou-date">{new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Status</p>
                  <p className="text-sm font-bold text-[#1a2744] mt-1" data-testid="thankyou-status">New (awaiting review)</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 mt-5">
                <p className="text-xs text-amber-900 leading-relaxed" data-testid="report-next-step-note"><strong>What happens next:</strong> {thanksCfg?.next_steps || 'Your report is logged with a W8 tracking number. The campaign team reviews new reports, watches for Ward 8 patterns, and follows up. If this needs official City action, also submit it through CKL ServiceHub, Report It, Customer Service, or the By-law Complaint Form. Then add your CKL case number to this report.'}</p>
              </div>
              <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3.5 mt-3 flex gap-2.5">
                <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700 leading-relaxed" data-testid="thankyou-privacy-note">{thanksCfg?.privacy_note || 'Your name, email, phone number, and exact address are never shown publicly.'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Next steps (prepared behind the scenes; campaign-reviewed wording) */}
          <Card className="border-[#C9A84C] bg-[#FFFDF6]" data-testid="thankyou-nextsteps-card">
            <CardContent className="p-5 sm:p-6">
              <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#C9A84C]" /> Your possible next steps</p>
              {thanksAiBusy ? (
                <div className="space-y-2 mt-3" data-testid="thankyou-nextsteps-loading">
                  <div className="h-4 bg-[#C9A84C]/15 rounded animate-pulse" />
                  <div className="h-4 bg-[#C9A84C]/15 rounded animate-pulse w-5/6" />
                  <div className="h-4 bg-[#C9A84C]/15 rounded animate-pulse w-2/3" />
                  <p className="text-xs text-slate-500 pt-1">Preparing your personalized summary…</p>
                </div>
              ) : thanksAi ? (
                <div className="space-y-4 mt-3" data-testid="thankyou-nextsteps-results">
                  <p className="text-sm text-slate-600 italic">{thanksAi.intro}</p>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">What you submitted</p>
                    <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-testid="thankyou-nextsteps-summary">{thanksAi.summary}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2.5 py-1 bg-slate-100 border border-slate-200 rounded-full font-bold text-slate-700" data-testid="thankyou-nextsteps-category">Likely category: {thanksAi.likely_category}</span>
                    <span className={`px-2.5 py-1 rounded-full font-bold border ${urgencyCls}`} data-testid="thankyou-nextsteps-urgency">Suggested urgency: {thanksAi.suggested_urgency}</span>
                  </div>
                  {(thanksAi.missing_info || []).length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Information that may still be missing</p>
                      <ul className="list-disc pl-5 text-sm text-slate-700 mt-1 space-y-1">
                        {thanksAi.missing_info.map((q, i) => <li key={`${i}-${q}`}>{q}</li>)}
                      </ul>
                    </div>
                  )}
                  {thanksAi.next_step && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Best next step</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-testid="thankyou-nextsteps-nextstep">{thanksAi.next_step}</p>
                    </div>
                  )}
                  {thanksAi.contact_path && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Likely City contact path</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed" data-testid="thankyou-nextsteps-contactpath">{thanksAi.contact_path}</p>
                    </div>
                  )}
                  {thanksAi.polished_request && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ready-to-send City request</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed whitespace-pre-wrap bg-white border border-slate-200 rounded-xl p-3" data-testid="thankyou-nextsteps-request">{thanksAi.polished_request}</p>
                    </div>
                  )}
                  {thanksAi.hub_note && <p className="text-xs text-slate-600 leading-relaxed" data-testid="thankyou-nextsteps-hubnote">{thanksAi.hub_note}</p>}
                  <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5 leading-relaxed" data-testid="thankyou-not-city-note">{thanksAi.not_city_note || 'This is a campaign accountability tool, not an official City of Kawartha Lakes service portal. For emergencies call 911.'}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">Keep your W8 tracking number safe and use Track My Issue for updates. If this needs official City action, submit it through CKL ServiceHub or Customer Service (705-324-9411).</p>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button onClick={copyRequest} className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="thankyou-copy-request-button">
              <Copy className="h-4 w-4 mr-1.5" /> Copy My City Request
            </Button>
            <Button asChild className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="thankyou-track-button">
              <Link to={`/issue-tracker/track?track=${result.tracking_number}`}><Search className="h-4 w-4 mr-1.5" /> Track This Issue</Link>
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <Button asChild variant="outline" className="h-12 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="thankyou-whocall-button">
                <Link to="/who-do-i-call"><Phone className="h-4 w-4 mr-1.5" /> Find Who to Call</Link>
              </Button>
              <Button variant="outline" className="h-12 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="thankyou-submit-another-button"
                onClick={() => { setResult(null); setThanksAi(null); setStep(1); setForm({ ...EMPTY_FORM }); window.scrollTo(0, 0); }}>
                Submit Another Issue
              </Button>
            </div>
            <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="thankyou-map-button">
              <Link to="/map"><Map className="h-4 w-4 mr-1.5" /> Public Issue Map</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader pageKey="report" title="REPORT A WARD 8 ISSUE." subtitle="Six quick steps. You'll get a W8 tracking number instantly." />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-4"><HubDisclaimer testId="report-disclaimer" /></div>

        <div className="mb-6">
          <p className="text-xs font-bold text-[#1a2744] mb-2" data-testid="report-step-label">Step {step} of 6: {STEPS[step - 1]}</p>
          <Progress value={(step / 6) * 100} className="h-2" />
        </div>

        <Card>
          <CardContent className="p-5 sm:p-8">
            {step === 1 && (
              <div className="space-y-5" data-testid="report-step-1">
                <p className="text-sm text-slate-600">Your contact details are <strong>never shown publicly</strong>. They let you securely track and update your own report.</p>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Full name <span className="text-red-600">*</span></label>
                  <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your full name" className="h-12" data-testid="report-name-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Email <span className="text-red-600">*</span></label>
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" className="h-12" data-testid="report-email-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Phone <span className="text-slate-400 font-normal text-sm">(optional)</span></label>
                  <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="705-000-0000" className="h-12" data-testid="report-phone-input" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="report-step-2">
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Issue category <span className="text-red-600">*</span></label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {Object.entries(liveCategories).map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => set('category', v)}
                        data-testid={`category-option-${v}`}
                        className={`px-3 py-3 rounded-xl border text-sm font-semibold text-left transition-colors duration-150 ${form.category === v ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#C9A84C]'}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Describe the issue <span className="text-red-600">*</span></label>
                  <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What's happening? How long has it been a problem? Who have you already contacted?" rows={6} data-testid="report-description-input" />
                </div>

                <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-4" data-testid="report-help-note">
                  <p className="text-sm font-bold text-[#1a2744]">Need help writing your report?</p>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">Please describe the issue in your own words. Include what happened, where it happened, when you noticed it, and whether you have already contacted the City.</p>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">Before anything is shown publicly, the campaign will review it to make sure private information is not displayed.</p>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">This is not an official City of Kawartha Lakes service portal.</p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5" data-testid="report-step-3">
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Address or closest location <span className="text-red-600">*</span></label>
                  <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Exact address or closest point (kept private)" className="h-12" data-testid="report-address-input" />
                  <p className="text-xs text-slate-500 mt-1">Kept private. Never shown publicly.</p>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Public location description <span className="text-red-600">*</span></label>
                  <Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Elm Tree Road near the bridge" className="h-12" data-testid="report-location-input" />
                  <p className="text-xs text-slate-500 mt-1">A general description others may see if you make the report public.</p>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Community or area</label>
                  <Select value={form.community} onValueChange={(v) => set('community', v)}>
                    <SelectTrigger className="h-12" data-testid="report-community-select"><SelectValue placeholder="Choose your community" /></SelectTrigger>
                    <SelectContent>
                      {communities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div data-testid="report-pin-section">
                  <label className="block font-bold text-[#1a2744] mb-1">Mark the exact spot on the map <span className="text-xs font-normal text-slate-500">(recommended)</span></label>
                  <p className="text-xs text-slate-600 mb-2 leading-relaxed">Your pin is the most accurate way to place this report. Zoom in and click where the issue is, drag to adjust, or use your location if you are standing there. If you skip this, we place the pin from the address you typed and check it by hand.</p>
                  <PinPlacer value={form.pin} onChange={(p) => set('pin', p)} exactPublic={form.pin_exact_public} onExactPublicChange={(v) => set('pin_exact_public', v)} testId="report-pin" />
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5" data-testid="report-step-4">
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Have you already contacted the City of Kawartha Lakes?</label>
                  <RadioGroup value={form.contacted_ckl ? 'yes' : 'no'} onValueChange={(v) => set('contacted_ckl', v === 'yes')} className="flex gap-4">
                    <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="ckl-yes" data-testid="report-ckl-yes" /><Label htmlFor="ckl-yes" className="font-semibold">Yes</Label></div>
                    <div className="flex items-center gap-2"><RadioGroupItem value="no" id="ckl-no" data-testid="report-ckl-no" /><Label htmlFor="ckl-no" className="font-semibold">No</Label></div>
                  </RadioGroup>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">CKL case number <span className="text-slate-400 font-normal text-sm">(optional)</span></label>
                  <Input value={form.ckl_case_number} onChange={(e) => set('ckl_case_number', e.target.value)} placeholder="If the City gave you a case number, add it here" className="h-12" data-testid="report-ckl-case-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Preferred follow-up method</label>
                  <RadioGroup value={form.preferred_followup} onValueChange={(v) => set('preferred_followup', v)} className="flex gap-4">
                    <div className="flex items-center gap-2"><RadioGroupItem value="email" id="fu-email" data-testid="report-followup-email" /><Label htmlFor="fu-email" className="font-semibold">Email</Label></div>
                    <div className="flex items-center gap-2"><RadioGroupItem value="phone" id="fu-phone" data-testid="report-followup-phone" /><Label htmlFor="fu-phone" className="font-semibold">Phone</Label></div>
                  </RadioGroup>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-5" data-testid="report-step-5">
                <label className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 cursor-pointer">
                  <Checkbox checked={form.consent_public} onCheckedChange={(v) => set('consent_public', !!v)} className="mt-0.5" data-testid="report-consent-checkbox" />
                  <span className="text-sm text-slate-700 leading-relaxed"><strong>Public visibility permission:</strong> show this report on the public issue board and map (generalized location only). Uncheck to keep it private to the campaign team.</span>
                </label>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Public title <span className="text-slate-400 font-normal text-sm">(optional)</span></label>
                  <Input value={form.public_title} onChange={(e) => set('public_title', e.target.value)} placeholder="Short public headline for the map/board" className="h-12" data-testid="report-public-title-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Public description <span className="text-slate-400 font-normal text-sm">(optional)</span></label>
                  <Textarea value={form.public_description} onChange={(e) => set('public_description', e.target.value)} placeholder="What should the public summary say? (The campaign reviews this before anything appears publicly.)" rows={3} data-testid="report-public-description-input" />
                </div>
                <label className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 cursor-pointer">
                  <Checkbox checked={form.photo_public} onCheckedChange={(v) => set('photo_public', !!v)} className="mt-0.5" data-testid="report-photo-permission-checkbox" />
                  <span className="text-sm text-slate-700 leading-relaxed"><strong>Public photo permission:</strong> allow uploaded photos to appear publicly with this report.</span>
                </label>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Photo / video upload <span className="text-slate-400 font-normal text-sm">(optional, up to 3)</span></label>
                  <div className="flex flex-wrap gap-3">
                    {form.photos.map((p, i) => (
                      <div key={p} className="relative">
                        <img src={fileUrl(p)} alt="Uploaded" className="h-20 w-20 object-cover rounded-lg border" />
                        <button type="button" onClick={() => set('photos', form.photos.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5" aria-label="Remove photo"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                    {form.photos.length < 3 && (
                      <label className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#C9A84C] text-slate-400">
                        {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                        <span className="text-[10px] font-bold mt-1">Add</span>
                        <input type="file" accept="image/*,video/*" className="hidden" onChange={handlePhoto} data-testid="report-photo-input" />
                      </label>
                    )}
                  </div>
                </div>
                <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-4 flex gap-3">
                  <ShieldCheck className="h-5 w-5 text-[#1a2744] shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-700 leading-relaxed" data-testid="report-privacy-note">Your name, email, phone number, exact address, private notes, and private messages will never be shown publicly.</p>
                </div>
              </div>
            )}

            {step === 6 && (
              <div className="space-y-4" data-testid="report-step-6">
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Your information (private)</p>
                  <p className="text-sm text-slate-700">{form.name} · {form.email}{form.phone ? ` · ${form.phone}` : ''} · prefers {form.preferred_followup}</p>
                </div>
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Issue summary</p>
                  <p className="text-sm font-bold text-[#1a2744]">{categoryLabel(form.category)}</p>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed">{form.description}</p>
                </div>
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Location</p>
                  <p className="text-sm text-slate-700">Private address: {form.address}</p>
                  <p className="text-sm text-slate-700">Public description: {form.location}{form.community ? ` · ${form.community}` : ''}</p>
                  <p className="text-sm font-bold mt-1" data-testid="review-pin-status">
                    {form.pin ? <span className="text-emerald-800">Map pin placed by you{form.pin_exact_public ? ' (shown exactly, public place)' : ' (shown about 100 to 150 m away for privacy)'}</span> : <span className="text-amber-800">No map pin placed. We will place it from your address and check it by hand.</span>}
                  </p>
                </div>
                {/* Ward detection (Phase 97): tell the resident which ward this is */}
                {wardChecking ? (
                  <div className="border border-slate-200 bg-slate-50 rounded-xl p-4 flex items-center gap-2" data-testid="report-ward-checking">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                    <p className="text-sm text-slate-600">Checking which ward this location is in…</p>
                  </div>
                ) : wardCheck && (
                  <div
                    className={`rounded-xl p-4 border ${wardCheck.needs_review
                      ? 'bg-amber-50 border-amber-200'
                      : wardCheck.ward === 8 ? 'bg-[#EAF2FF] border-[#CFE0FF]' : 'bg-amber-50 border-amber-200'}`}
                    data-testid="report-ward-message"
                  >
                    <p className="text-sm font-bold text-[#1a2744] flex items-center gap-2">
                      <Map className="h-4 w-4 shrink-0" />
                      {wardCheck.needs_review ? 'Location needs manual review' : `This location is in Ward ${wardCheck.ward}.`}
                    </p>
                    {(wardCheck.needs_review || wardCheck.ward !== 8) && (
                      <p className="text-xs text-slate-700 mt-1 leading-relaxed" data-testid="report-ward-message-text">{wardCheck.message}</p>
                    )}
                  </div>
                )}
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Public map preview</p>
                  {form.consent_public ? (
                    <div data-testid="report-public-preview">
                      <p className="text-sm font-bold text-[#1a2744]">{form.public_title || (form.cleaned_summary || form.description).slice(0, 90)}</p>
                      <p className="text-sm text-slate-700 mt-1">{form.public_description || form.public_map_summary || 'General summary will appear here.'}</p>
                      <p className="text-xs text-slate-500 mt-1">Area: {form.community || form.location} · Photos public: {form.photo_public ? 'Yes' : 'No'}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-700">This report will stay <strong>private</strong>. It will not appear on the public board or map.</p>
                  )}
                </div>
                <div className="bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-4 flex gap-3">
                  <ShieldCheck className="h-5 w-5 text-[#1a2744] shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-700 leading-relaxed">Privacy confirmation: your name, email, phone, exact address, private notes, and private messages will never be shown publicly.</p>
                </div>
                <label className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 cursor-pointer">
                  <Checkbox checked={form.consent_confirmed} onCheckedChange={(v) => set('consent_confirmed', !!v)} className="mt-0.5" data-testid="report-final-consent-checkbox" />
                  <span className="text-sm text-slate-700 leading-relaxed"><span className="text-red-600">*</span> I confirm this report is accurate to the best of my knowledge, and I consent to the campaign storing my contact details to follow up on this issue.</span>
                </label>
              </div>
            )}

            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="h-12 px-5 font-bold btn-press" data-testid="issue-report-back"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
              ) : (
                <Button variant="outline" onClick={() => navigate('/issue-tracker')} className="h-12 px-5 font-bold btn-press">Cancel</Button>
              )}
              {step < 6 ? (
                <Button onClick={() => canNext && setStep(step + 1)} disabled={!canNext} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="issue-report-next">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
              ) : (
                <Button onClick={submit} disabled={busy || !form.consent_confirmed} className="h-12 px-6 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="issue-report-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Submit Report
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 mt-4 text-center leading-relaxed">For emergencies call 911. This hub is a campaign accountability tool, not an official City service channel.</p>
      </div>
    </div>
  );
}
